import { useState, useEffect } from 'react';

const NAVY = '#071E5B';
const NAVY_DARK = '#041642';
const GOLD = '#DDBB86';
const LAVENDER = '#A789E6';
const LAVENDER_DARK = '#8F73D5';
const LP_URL = 'https://oclubedoeu.com.br/jornada-ao-oceano';

// SVG icons
const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const KeyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7.5" cy="15.5" r="5.5"/>
    <path d="M21 2l-9.6 9.6"/>
    <path d="M15.5 7.5l3 3L22 7l-3-3"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const ExternalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

function parseDate(str) {
  if (!str) return null;
  // Supports YYYY-MM-DD
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function today() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function diffDays(a, b) {
  return Math.round((b - a) / 86400000);
}

export default function JornadaCardHome({ jornada, onNavigate }) {
  const [cardState, setCardState] = useState(null);

  useEffect(() => {
    if (!jornada) return;

    const now = today();
    const enrollmentOpen = parseDate(jornada.enrollment_open_date);
    const firstEncounter = parseDate(jornada.first_encounter_date);
    const started = jornada.status === 'active' || jornada.status === 'started';

    let state;
    if (started) {
      state = 'started';
    } else if (firstEncounter && diffDays(now, firstEncounter) <= 2) {
      state = 'imminent'; // 2 days before first encounter
    } else if (enrollmentOpen && now >= enrollmentOpen) {
      state = 'enrollment_open';
    } else {
      state = 'coming_soon';
    }
    setCardState(state);
  }, [jornada]);

  if (!jornada || !jornada.visible) return null;

  const handlePrimary = () => {
    if (cardState === 'imminent') {
      // "Tenho o código de acesso" → go to jornada page for code entry
      onNavigate('jornada');
    }
    // 'coming_soon' and 'started' primary is disabled
  };

  const handleSecondary = () => {
    if (cardState === 'started') {
      onNavigate('jornada');
    } else if (cardState === 'enrollment_open' || cardState === 'imminent') {
      window.open(LP_URL, '_blank', 'noopener');
    }
    // 'coming_soon' is disabled
  };

  const primaryLabel = () => {
    if (cardState === 'imminent') return 'Tenho o código de acesso';
    return 'Em breve';
  };

  const primaryDisabled = cardState === 'coming_soon' || cardState === 'started' || cardState === null;
  const primaryIcon = cardState === 'imminent' ? <KeyIcon /> : <ClockIcon />;

  const secondaryLabel = () => {
    if (cardState === 'started') return 'Entrar na lista de espera';
    return 'Fazer minha inscrição';
  };

  const secondaryDisabled = cardState === 'coming_soon' || cardState === null;
  const secondaryIcon = cardState === 'started' ? <UsersIcon /> : <ExternalIcon />;

  return (
    <div style={{ ...styles.card, cursor: cardState === 'coming_soon' ? 'default' : 'pointer' }} onClick={() => cardState !== 'coming_soon' && onNavigate('jornada')}>
      {/* Thumbnail */}
      <div style={styles.thumbWrap}>
        <div style={styles.thumb} />
      </div>

      {/* Content — sobrepõe levemente a imagem */}
      <div style={styles.content}>
        <p style={styles.subtitle}>Mentoria em grupo · 8 encontros</p>

        <div style={styles.buttons}>
          {/* Primary button */}
          <button
            style={{
              ...styles.btnPrimary,
              opacity: primaryDisabled ? 0.45 : 1,
              cursor: primaryDisabled ? 'not-allowed' : 'pointer',
            }}
            onClick={handlePrimary}
            disabled={primaryDisabled}
          >
            <span style={styles.btnIcon}>{primaryIcon}</span>
            {primaryLabel()}
          </button>

          {/* Secondary button */}
          <button
            style={{
              ...styles.btnSecondary,
              opacity: secondaryDisabled ? 0.45 : 1,
              cursor: secondaryDisabled ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSecondary}
            disabled={secondaryDisabled}
          >
            <span style={styles.btnIcon}>{secondaryIcon}</span>
            {secondaryLabel()}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: {
    borderRadius: 28,
    background: `linear-gradient(180deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
    overflow: 'hidden',
    margin: '0 0 8px',
  },
  thumbWrap: {
    position: 'relative',
  },
  thumbFade: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '40%',
    background: `linear-gradient(to bottom, transparent, ${NAVY})`,
    pointerEvents: 'none',
  },
  thumb: {
    width: '100%',
    height: 160,
    backgroundImage: "url('/jornada-hero.png')",
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
    display: 'block',
  },
  thumbFade: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '55%',
    background: `linear-gradient(to bottom, transparent, ${NAVY_DARK})`,
    pointerEvents: 'none',
  },
  content: {
    padding: '6px 20px 16px',
    marginTop: -8,
  },
  subtitle: {
    color: GOLD,
    fontSize: 13,
    fontWeight: 500,
    margin: '0 0 10px',
    letterSpacing: '0.02em',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingLeft: 12,
    paddingRight: 12,
  },
  btnPrimary: {
    height: 44,
    borderRadius: 14,
    background: `linear-gradient(90deg, ${LAVENDER_DARK}, ${LAVENDER})`,
    color: '#fff',
    fontSize: 16,
    fontWeight: 600,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'opacity 0.2s',
  },
  btnSecondary: {
    height: 44,
    borderRadius: 14,
    background: 'transparent',
    border: `1.5px solid rgba(167,137,230,0.85)`,
    color: '#D7C8F5',
    fontSize: 16,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'opacity 0.2s',
  },
  btnIcon: {
    display: 'flex',
    alignItems: 'center',
  },
};
